import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { CategoryBadge, SourceBadges } from './ConceptBadges'
import { WeightLabel } from './ArtworkTile'
import { RELATION_LABEL } from './ruleText'
import { CATEGORIES, RELATION_TYPES, type Proposal, type ProposalConcept, type RelationType } from '../api/knowledgeApi'
import { useAcceptProposal, useRejectProposal } from '../hooks/useKnowledge'

function Thumbs({ ids, size = 'h-14 w-14' }: { ids: number[]; size?: string }) {
  if (!ids.length) return null
  return (
    <div className="flex gap-1">
      {ids.map((id) => (
        <Link key={id} to={`/app/artworks/${id}`} className={`${size} shrink-0 overflow-hidden rounded border bg-slate-100`}>
          <img src={artworkApi.getArtworkImageUrl(id)} alt="" loading="lazy" className="h-full w-full object-cover" />
        </Link>
      ))}
    </div>
  )
}

function ConceptBlock({ c, highlight }: { c: ProposalConcept; highlight?: string[] }) {
  if (c.missing) return <div className="text-sm text-slate-400">{c.ucid} (no longer in the graph)</div>
  const others = c.memberNames.filter((m) => m !== c.name)
  return (
    <div className="min-w-0 space-y-1.5">
      <div>
        <Link to={`/app/knowledge/${c.ucid}`} className="font-medium hover:underline">{c.name}</Link>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <CategoryBadge category={c.category} />
          <SourceBadges sources={c.sources} />
          <span>{c.artworkCount.toLocaleString()} artworks</span>
          <span>weight <WeightLabel weight={c.weight} /></span>
        </div>
      </div>
      {others.length > 0 && (
        <div className="text-xs text-slate-500">
          also:{' '}
          {others.slice(0, 6).map((m, i) => (
            <span key={m} className={highlight?.includes(m) ? 'rounded bg-rose-100 px-0.5 text-rose-800' : undefined}>
              {i > 0 && ', '}{m}
            </span>
          ))}
          {others.length > 6 && ` +${others.length - 6}`}
        </div>
      )}
      <Thumbs ids={c.artworkIds} />
    </div>
  )
}

/**
 * One model proposal: what the model wants to change, its reasoning, the evidence (concept cards
 * with thumbnails), and accept / reject. Adjustable before accepting where it makes sense: the
 * merged name, the category, which names to split off, the relation type.
 */
export function ProposalCard({ p, selectable, selected, onToggle }: {
  p: Proposal
  selectable?: boolean
  selected?: boolean
  onToggle?: () => void
}) {
  const accept = useAcceptProposal()
  const reject = useRejectProposal()
  const [name, setName] = useState(p.payload.canonicalName ?? '')
  const [category, setCategory] = useState(p.payload.category ?? '')
  const [split, setSplit] = useState<string[]>(p.payload.splitNames ?? [])
  const [relation, setRelation] = useState<RelationType>(p.payload.relationType ?? 'related_to')
  const busy = accept.isPending || reject.isPending
  const pending = p.status === 'pending'
  const [first, second] = p.concepts

  const edits = () => {
    if (p.action === 'merge') return { canonicalName: name.trim() || null }
    if (p.action === 'category' && category && category !== p.payload.category) return { category }
    if (p.action === 'split') return { splitNames: split }
    if (p.action === 'link' && p.payload.operation !== 'remove' && relation !== p.payload.relationType) return { relationType: relation }
    return {}
  }

  return (
    <div className="space-y-3 border-b py-4 last:border-0">
      <div className="flex items-start gap-3">
        {selectable && <Checkbox className="mt-1" checked={selected} onCheckedChange={onToggle} aria-label="Select" />}
        <div className="min-w-0 flex-1 space-y-3">
          {p.action === 'merge' && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {p.concepts.map((c) => <ConceptBlock key={c.ucid} c={c} />)}
              </div>
              {pending && (
                <div className="flex max-w-md items-center gap-2 text-sm">
                  <span className="shrink-0 text-slate-500">Merged name</span>
                  <Input className="h-8" value={name} onChange={(e) => setName(e.target.value)} placeholder="keep the build's name" />
                </div>
              )}
            </>
          )}
          {p.action === 'split' && first && (
            <>
              <ConceptBlock c={first} highlight={split} />
              {pending && (
                <div className="flex flex-wrap gap-2">
                  {first.memberNames.map((m) => (
                    <label key={m} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${split.includes(m) ? 'border-rose-300 bg-rose-50' : 'bg-white'}`}>
                      <Checkbox checked={split.includes(m)} onCheckedChange={(v) => setSplit(v ? [...split, m] : split.filter((x) => x !== m))} />
                      {m}
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          {p.action === 'link' && first && second && (
            <div className="space-y-2">
              <div className="text-sm font-medium">
                {p.payload.operation === 'remove' ? 'Remove relationship' : p.payload.operation === 'replace' ? 'Replace relationship' : 'Add relationship'}
              </div>
              {p.payload.previousRelation && <p className="text-xs text-slate-500">Current: {p.concepts.find(c => c.ucid === p.payload.previousRelation?.sourceUcid)?.name} → {RELATION_LABEL[p.payload.previousRelation.relationType]} → {p.concepts.find(c => c.ucid === p.payload.previousRelation?.targetUcid)?.name}</p>}
            <div className="grid items-start gap-3 sm:grid-cols-[1fr_auto_1fr]">
              <ConceptBlock c={first} />
              <div className="flex flex-col items-center gap-1 pt-1 text-xs text-slate-500">
                {pending && p.payload.operation !== 'remove' ? (
                  <Select value={relation} onValueChange={(v) => setRelation(v as RelationType)}>
                    <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RELATION_TYPES.map((r) => <SelectItem key={r} value={r}>{RELATION_LABEL[r]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <span>{RELATION_LABEL[relation]}</span>
                )}
                <ArrowRight className="h-4 w-4" />
              </div>
              <ConceptBlock c={second} />
            </div>
            </div>
          )}
          {(p.action === 'category' || p.action === 'generic' || p.action === 'reject') && first && (
            <div className="flex flex-wrap items-start justify-between gap-3">
              <ConceptBlock c={first} />
              {p.action === 'category' && pending && (
                <Select value={category || undefined} onValueChange={setCategory}>
                  <SelectTrigger className="h-8 w-40"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}{c === p.payload.category ? ' (proposed)' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {p.action === 'generic' && (
                <span className="text-sm text-slate-600">set weight to <WeightLabel weight={p.payload.suggestedWeight ?? 0.1} /></span>
              )}
              {p.action === 'reject' && <span className="text-sm text-rose-700">remove from the graph</span>}
            </div>
          )}

          {!!p.payload.sourceEvidence?.length && <div className="space-y-2 rounded-md border border-arthaus-gold/20 p-3">
            <p className="text-xs font-medium">Research evidence · candidate alignments</p>
            {p.payload.sourceEvidence.map(e => <div key={e.id} className="text-xs text-slate-500">
              {/^https?:\/\//.test(e.sourceUri) ? <a href={e.sourceUri} target="_blank" rel="noopener noreferrer" className="underline">{e.name}</a> : e.name}
              <span> · {e.scope || 'scope unspecified'}{e.claimRelation ? ` · ${e.claimRelation}` : ''}{e.polarity ? ` · ${e.polarity.replace(/_/g, ' ')}` : ''}{e.targetId ? ` · ${e.targetId}` : ''} · {(e.semanticEntailment || 'pending review').replace(/_/g, ' ')}</span>
            </div>)}
          </div>}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <p className="max-w-3xl text-sm text-slate-600">
              {p.reasoning}
              {p.confidence != null && <span className="ml-1.5 text-xs tabular-nums text-slate-400">confidence {Math.round(p.confidence * 100)}%</span>}
            </p>
            {pending ? (
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm"
                  disabled={busy || (p.action === 'split' && (!split.length || split.length === (first?.memberNames.length ?? 0)))}
                  onClick={() => accept.mutate({ id: p.proposalId, action: p.action, edits: edits() })}
                >
                  <Check className="mr-1.5 h-3.5 w-3.5" /> {p.payload.operation === 'remove' ? 'Accept removal' : p.payload.operation === 'replace' ? 'Accept replacement' : 'Accept'}
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => reject.mutate({ id: p.proposalId, action: p.action })}>
                  <X className="mr-1.5 h-3.5 w-3.5" /> {p.action === 'merge' ? 'Different' : 'Reject'}
                </Button>
              </div>
            ) : (
              <span className="text-xs text-slate-500">{p.status}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
